import React, { useState, useEffect } from 'react';
import api from '../../../../config/axios';
import Swal from 'sweetalert2';
import { FaPlus, FaEdit, FaTrash, FaFilter, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SubLancamentoModal from './modals/SubLancamento';
import Paginacao from '../../../../components/utils/paginacao';

const SubLancamentos = () => {
    const navigate = useNavigate();
    const [sublancamentos, setSublancamentos] = useState([]);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;
    const [showModal, setShowModal] = useState(false);
    const [selectedSublancamento, setSelectedSublancamento] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroFormaPagamento, setFiltroFormaPagamento] = useState('');

    useEffect(() => {
        fetchCategorias();
    }, []);

    useEffect(() => {
        fetchSublancamentos(currentPage);
    }, [currentPage]);

    const fetchSublancamentos = async (page) => {
        try {
            Swal.fire({
                title: 'Carregando...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                page: page,
                limit: limit
            };

            if (filtroCategoria) params.categoria_principal_id = filtroCategoria;
            if (filtroFormaPagamento) params.forma_pagamento = filtroFormaPagamento;

            const response = await api.get('/sublancamentos', { params });
            setSublancamentos(response.data.records || []);
            setTotalRegistros(response.data.totalRegistros || 0);
            setTotalPages(Math.ceil((response.data.totalRegistros || 0) / limit) || 1);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar sublançamentos', 'error');
        } finally {
            Swal.close();
        }
    };

    const fetchCategorias = async () => {
        try {
            const response = await api.get('/categorias-financeiras?limit=1000');
            setCategorias(response.data.records || []);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar categorias', 'error');
        }
    };

    const handleNew = () => {
        setSelectedSublancamento(null);
        setShowModal(true);
    };

    const handleEdit = async (grupo) => {
        try {
            const response = await api.get('/sublancamentos', {
                params: {
                    categoria_principal_id: grupo.categoria_principal_id,
                    limit: 1000
                }
            });
            setSelectedSublancamento({
                categoria_principal_id: grupo.categoria_principal_id,
                sublancamentos: response.data.records || []
            });
            setShowModal(true);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar sublançamentos', 'error');
        }
    };

    const handleDelete = async (grupo) => {
        try {
            const result = await Swal.fire({
                title: 'Confirmar Exclusão',
                html: `Deseja realmente excluir todos os sublançamentos da categoria <strong>${grupo.categoriaPrincipal?.nome}</strong>?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Excluindo...',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await api.get('/sublancamentos', {
                    params: {
                        categoria_principal_id: grupo.categoria_principal_id,
                        limit: 1000
                    }
                });

                const sublancamentosParaExcluir = response.data.records || [];

                for (const sub of sublancamentosParaExcluir) {
                    await api.delete(`/sublancamentos/${sub.id}`);
                }

                await Swal.fire('Sucesso', 'Sublançamentos excluídos com sucesso', 'success');
                fetchSublancamentos(currentPage);
            }
        } catch (error) {
            Swal.close();
            Swal.fire('Erro', error.response?.data?.message || 'Erro ao excluir sublançamentos', 'error');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSublancamento(null);
    };

    const handleSublancamentoAtualizado = () => {
        fetchSublancamentos(currentPage);
        handleCloseModal();
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFiltrar = () => {
        setCurrentPage(1);
        fetchSublancamentos(1);
    };

    const handleLimparFiltros = () => {
        setFiltroCategoria('');
        setFiltroFormaPagamento('');
    };

    const getFormaPagamentoLabel = (forma) => {
        if (!forma) return 'Todas';
        const formas = {
            'cartao_credito': 'Cartão de Crédito',
            'cartao_debito': 'Cartão de Débito',
            'pix': 'PIX',
            'dinheiro': 'Dinheiro',
            'cheque': 'Cheque'
        };
        return formas[forma] || forma;
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-12">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn btn-outline-secondary me-3"
                                    onClick={() => navigate('/financeiro/cadastros')}
                                    title="Voltar para Cadastros"
                                >
                                    <FaArrowLeft />
                                </button>
                                <h4 className="fw-bold mb-0">Sublançamentos</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3 mb-3">
                        <div className="col-md-6 col-12">
                            <button className="btn btn-outline-primary" onClick={handleNew}>
                                <FaPlus /> Adicionar Sublançamento
                            </button>
                        </div>
                    </div>

                    <div className="row mt-4 mb-4">
                        <div className="col-lg-4 col-12">
                            <label className="form-label fw-bold">Categoria Principal</label>
                            <select
                                className="form-control"
                                value={filtroCategoria}
                                onChange={(e) => setFiltroCategoria(e.target.value)}
                            >
                                <option value="">Todas</option>
                                {categorias.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Forma de Pagamento</label>
                            <select
                                className="form-control"
                                value={filtroFormaPagamento}
                                onChange={(e) => setFiltroFormaPagamento(e.target.value)}
                            >
                                <option value="">Todas</option>
                                <option value="cartao_credito">Cartão de Crédito</option>
                                <option value="cartao_debito">Cartão de Débito</option>
                                <option value="pix">PIX</option>
                                <option value="dinheiro">Dinheiro</option>
                            </select>
                        </div>
                        <div className="col-lg-5 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                            <div className="d-flex gap-2 w-100 justify-content-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleLimparFiltros}
                                >
                                    Limpar
                                </button>
                                <button
                                    className="btn btn-custom-primary"
                                    onClick={handleFiltrar}
                                >
                                    <FaFilter /> Filtrar
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead className="table-custom-primary">
                                <tr>
                                    <th className="text-nowrap">Categoria</th>
                                    <th className="text-nowrap">Quantidade de Sublançamentos</th>
                                    <th className="text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sublancamentos.length > 0 ? (
                                    sublancamentos.map((grupo) => (
                                        <tr key={grupo.categoria_principal_id}>
                                            <td className="text-nowrap">{grupo.categoriaPrincipal?.nome}</td>
                                            <td className="text-nowrap">{grupo.quantidade}</td>
                                            <td className="text-center text-nowrap">
                                                <button
                                                    className="btn btn-custom-secondary btn-sm"
                                                    onClick={() => handleEdit(grupo)}
                                                    title="Editar"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="btn btn-custom-secondary btn-sm ms-3"
                                                    onClick={() => handleDelete(grupo)}
                                                    title="Excluir todos os sublançamentos desta categoria"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center">
                                            Nenhum sublançamento cadastrado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="mb-3">
                            <span>
                                Total de registros: <strong>{totalRegistros}</strong>
                            </span>
                        </div>
                        <Paginacao
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            {showModal && (
                <SubLancamentoModal
                    sublancamento={selectedSublancamento}
                    onClose={handleCloseModal}
                    onSublancamentoAtualizado={handleSublancamentoAtualizado}
                />
            )}
        </div>
    );
};

export default SubLancamentos;
